// src/contexts/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../../shared/supabase';
import SessionExpiryModal from '../components/SessionExpiryModal';
import {
  clearSupabaseAuthStorage,
  isAuthLockError,
  runAuthOperation,
  withAuthRetry,
} from '../utils/authLockUtils';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionModal, setShowSessionModal] = useState(false);
  // The auth effect below runs once ([] deps), so its onAuthStateChange/getSession
  // closures capture the first-render value of any state (always its initial
  // value). A ref always reflects the latest value, so the session-expiry modal
  // guards read it instead. (Was previously a useState whose stale closure value
  // kept the expiry modal from ever showing.) Nothing renders from this, so a
  // ref — not state — is the right tool.
  const wasAuthenticatedRef = useRef(false);
  const updateWasAuthenticated = (value) => {
    wasAuthenticatedRef.current = value;
  };

  useEffect(() => {
    let mounted = true;

    const getSession = async (isRetry = false) => {
      const safetyTimer = setTimeout(() => {
        if (mounted && loading) {
          setLoading(false);
        }
      }, 5000);

      try {
        // Safely fetch session (detectSessionInUrl: true is handled by SDK)
        const result = await withAuthRetry(() => supabase.auth.getSession());
        const session = result?.data?.session;
        const authError = result?.error;

        if (authError) throw authError;

        if (mounted) {
          const hasUser = !!session?.user;
          setUser(session?.user ?? null);
          updateWasAuthenticated(hasUser);
          setLoading(false);
        }
      } catch (error) {
        // Web Locks / processLock contention — retry without wiping session storage.
        if (isAuthLockError(error) && !isRetry) {
          clearTimeout(safetyTimer);
          await new Promise((r) => setTimeout(r, 300));
          return getSession(true);
        }

        // AbortError from a stale/incompatible session can poison subsequent requests.
        // Only clear storage for non-lock aborts.
        const isAbort = error?.name === 'AbortError' || error?.message?.includes('aborted');
        if (isAbort && !isRetry && !isAuthLockError(error)) {
          try {
            // Clear all heartbeat/auth keys from localStorage
            Object.keys(localStorage)
              .filter((k) => k.toLowerCase().includes('sb-') || k.includes('supabase'))
              .forEach((k) => localStorage.removeItem(k));

            // Clear supabase cookies too
            try {
              const cookies = document.cookie.split(';');
              for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i];
                const eqPos = cookie.indexOf('=');
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                if (name.includes('sb-') || name.includes('supabase')) {
                  document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
                  document.cookie =
                    name +
                    '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' +
                    window.location.hostname;
                }
              }
            } catch (e) {
              /* ignore cookie errors */
            }
          } catch {
            /* ignore storage errors */
          }

          if (mounted) {
            clearTimeout(safetyTimer);
            await new Promise((r) => setTimeout(r, 600));
            return getSession(true);
          }
        }

        if (
          typeof error?.message === 'string' &&
          (error.message.includes('Invalid Refresh Token') ||
            error.message.includes('Refresh Token Not Found'))
        ) {
          try {
            await runAuthOperation(() => supabase.auth.signOut(), {
              maxAttempts: 1,
              timeoutMs: 5000,
            });
          } catch {
            clearSupabaseAuthStorage();
          }
          if (mounted && wasAuthenticatedRef.current) setShowSessionModal(true);
        }
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
      } finally {
        clearTimeout(safetyTimer);
      }
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // CRITICAL: never await another supabase.* call inside this callback.
      // auth-js holds its internal lock while notifying subscribers, and any
      // nested Supabase call (rpc, query, signOut, ...) needs getSession(),
      // which queues behind that same lock → permanent deadlock that freezes
      // every query in the tab. Defer such work to a macrotask instead.
      if (mounted) {
        setUser(session?.user ?? null);
        setLoading(false);
      }

      if (event === 'TOKEN_REFRESH_FAILED') {
        setTimeout(async () => {
          try {
            await runAuthOperation(() => supabase.auth.signOut(), {
              maxAttempts: 1,
              timeoutMs: 5000,
            });
          } catch {
            clearSupabaseAuthStorage();
          }
          if (mounted && wasAuthenticatedRef.current) setShowSessionModal(true);
        }, 0);
      }

      if (event === 'SIGNED_IN') {
        updateWasAuthenticated(true);
        // Attempt to sync guest orders — silently skip if RPC doesn't exist
        setTimeout(async () => {
          try {
            await supabase.rpc('sync_guest_orders').throwOnError();
          } catch {
            // RPC may not exist on all environments — ignore completely
          }
        }, 0);
      }

      if (event === 'SIGNED_OUT') {
        // Only show modal if we were previously authenticated and not already on an auth page
        const isAuthPage = ['/login', '/signup', '/forgot-password'].some((path) =>
          window.location.pathname.includes(path),
        );
        if (mounted && wasAuthenticatedRef.current && !isAuthPage) {
          setShowSessionModal(true);
        }
        updateWasAuthenticated(false);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const handleLoginSuccess = () => setShowSessionModal(false);

  // Explicitly re-reads the session from Supabase and updates state.
  // Call this after signInWithPassword so the user is set before navigation.
  const refreshUser = async () => {
    try {
      const { data, error } = await withAuthRetry(() => supabase.auth.getSession());
      if (error) throw error;
      setUser(data.session?.user ?? null);
    } catch (error) {
      console.warn('[AuthContext] refreshUser failed to refresh user session:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser }}>
      {children}
      <SessionExpiryModal open={showSessionModal} onLoginSuccess={handleLoginSuccess} />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
